export const DRIVE_TIME_BUCKET = '17:00-18:00'

const normalize = (value) => String(value || '').trim().toLowerCase()

const uniqueStudents = (students) => {
    const seen = new Set()
    return (Array.isArray(students) ? students : []).filter((student) => {
        const key = String(student?.studentId || `${student?.familyId}:${student?.studentName}`)
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

export const buildSameInstrumentDriveTimeGroups = (summary) => (
    (summary?.schools || []).flatMap((school) => (
        (school?.instruments || []).map((instrument) => {
            const students = uniqueStudents(instrument?.students)
                .filter((student) => student?.teacherPlan === 'needs-new-teacher')

            return {
                key: `${school.id}:${normalize(instrument.name)}`,
                schoolId: school.id,
                schoolName: school.name,
                instrumentName: instrument.name,
                students,
            }
        })
    )).filter((group) => group.students.length >= 2)
)

export const formatFamilyAddress = (address) => {
    if (typeof address === 'string') return address.trim()
    if (!address || typeof address !== 'object') return ''

    const normalizedAddress = address.Address || address.address || address

    return [
        normalizedAddress.addressLine1
            || normalizedAddress.AddressLine1
            || normalizedAddress.StreetAddress
            || normalizedAddress.streetAddress,
        normalizedAddress.addressLine2
            || normalizedAddress.AddressLine2
            || normalizedAddress.unit,
        normalizedAddress.city || normalizedAddress.City,
        normalizedAddress.state || normalizedAddress.State,
        normalizedAddress.zipcode
            || normalizedAddress.Zipcode
            || normalizedAddress.Zip
            || normalizedAddress.zipCode,
    ].map((value) => String(value || '').trim()).filter(Boolean).join(', ')
}

export const resolveFamilyPlaceKey = (student, directories) => {
    const familyId = String(student?.familyId || '').trim()
    if (!familyId) return ''

    const matchingDirectory = (Array.isArray(directories) ? directories : []).find((directory) => (
        (Array.isArray(directory?.familyIds) && directory.familyIds.some(
            (savedFamilyId) => String(savedFamilyId) === familyId
        ))
        || normalize(directory?.key) === normalize(familyId)
    ))

    return String(matchingDirectory?.key || '').trim()
}

const buildEdgeLookup = (edges, bucket) => {
    const lookup = new Map()

    ;(Array.isArray(edges) ? edges : []).forEach((edge) => {
        if (edge?.todBucket && edge.todBucket !== bucket) return

        const fromKey = edge?.origin?.key || edge?.fromKey || edge?.from
        const toKey = edge?.destination?.key || edge?.toKey || edge?.to
        const minutes = Number(edge?.minutes)
        if (!fromKey || !toKey || !Number.isFinite(minutes)) return

        lookup.set(`${normalize(fromKey)}=>${normalize(toKey)}`, minutes)
    })

    return lookup
}

export const buildDriveTimeRows = (
    group,
    directories = [],
    edges = [],
    bucket = DRIVE_TIME_BUCKET
) => {
    const edgeLookup = buildEdgeLookup(edges, bucket)
    const rows = []

    group.students.forEach((fromStudent, fromIndex) => {
        group.students.slice(fromIndex + 1).forEach((toStudent) => {
            const sameFamily = (
                fromStudent.familyId
                && String(fromStudent.familyId) === String(toStudent.familyId)
            )
            const fromKey = resolveFamilyPlaceKey(fromStudent, directories)
            const toKey = resolveFamilyPlaceKey(toStudent, directories)
            const fromToMinutes = sameFamily
                ? 0
                : edgeLookup.get(`${normalize(fromKey)}=>${normalize(toKey)}`)
            const toFromMinutes = sameFamily
                ? 0
                : edgeLookup.get(`${normalize(toKey)}=>${normalize(fromKey)}`)
            const knownMinutes = [fromToMinutes, toFromMinutes]
                .filter((minutes) => Number.isFinite(minutes))

            let displayTime = 'Not calculated'
            let status = 'not-calculated'
            if (sameFamily) {
                displayTime = 'Same home'
                status = 'available'
            } else if (!fromKey || !toKey) {
                displayTime = 'Missing family location'
                status = 'missing-location'
            } else if (knownMinutes.length > 0) {
                const shortest = Math.min(...knownMinutes)
                const longest = Math.max(...knownMinutes)
                displayTime = shortest === longest
                    ? `${shortest} min`
                    : `${shortest}–${longest} min`
                status = 'available'
            }

            rows.push({
                key: `${fromStudent.studentId}:${toStudent.studentId}`,
                fromStudent,
                toStudent,
                fromKey,
                toKey,
                fromToMinutes,
                toFromMinutes,
                displayTime,
                status,
            })
        })
    })

    return rows
}

export const buildMissingDirectedPairs = (rows) => {
    const pairs = new Map()

    rows.forEach((row) => {
        if (!row.fromKey || !row.toKey || row.fromKey === row.toKey) return

        if (!Number.isFinite(row.fromToMinutes)) {
            pairs.set(`${row.fromKey}=>${row.toKey}`, {
                fromKey: row.fromKey,
                toKey: row.toKey,
            })
        }
        if (!Number.isFinite(row.toFromMinutes)) {
            pairs.set(`${row.toKey}=>${row.fromKey}`, {
                fromKey: row.toKey,
                toKey: row.fromKey,
            })
        }
    })

    return Array.from(pairs.values())
}
